import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './App';
import { debounce } from './utils/DebounceUtils';

const FireAuthContext = createContext();

const roundToTwoDecimals = (number) => {
  if (isNaN(number) || number === undefined) {
    return 0;
  }
  return parseFloat(number.toFixed(2));
};

export const FireAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const lastUpdateTimeRef = useRef(0);
  const UPDATE_INTERVAL = 500;

  const fetchUserData = useCallback(async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        return userDocSnap.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }, []);

  const updateUser = useCallback(async () => {
    const debouncedUpdate = debounce(async () => {
      const currentTime = Date.now();
      if (currentTime - lastUpdateTimeRef.current < UPDATE_INTERVAL) {
        return;
      }

      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        const userData = await fetchUserData(currentUser.uid);
        if (userData) {
          if (userData.authorized === false) {
            await signOut(auth);
            setUser(null);
          } else {
            setUser((prevUser) => ({
              ...userData,
              money: roundToTwoDecimals(userData.money),
              crypto: {
                ...userData.crypto,
                hahumCoin: roundToTwoDecimals(userData.crypto?.hahumCoin),
              },
              inventory: userData.inventory,
              uid: currentUser.uid,
              email: currentUser.email,
              emailVerified: currentUser.emailVerified,
              lastSignInTime: currentUser.metadata.lastSignInTime,
            }));
          }
          lastUpdateTimeRef.current = currentTime;
          setLastUpdated(currentTime);
        }
      } else {
        setUser(null);
      }
    }, 100);

    debouncedUpdate();
  }, [fetchUserData]);

  const updateUserField = useCallback(async (field, value) => {
    if (user && user.uid) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, { [field]: value });
        setUser((prevUser) => ({
          ...prevUser,
          [field]: value,
        }));
        await updateUser();
      } catch (error) {
        console.error(`Error updating field ${field}:`, error);
      }
    }
  }, [user, updateUser]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      if (firebaseUser) {
        const userData = await fetchUserData(firebaseUser.uid);
        if (userData) {
          if (userData.authorized === false) {
            await signOut(auth);
            setUser(null);
          } else {
            setUser({
              ...userData,
              money: roundToTwoDecimals(userData.money),
              crypto: {
                ...userData.crypto,
                hahumCoin: roundToTwoDecimals(userData.crypto?.hahumCoin),
              },
              inventory: userData.inventory,
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              emailVerified: firebaseUser.emailVerified,
              lastSignInTime: firebaseUser.metadata.lastSignInTime,
            });
          }
        }
      } else {
        setUser(null);
      }
      setLoading(false);
      setLastUpdated(Date.now());
    });

    return () => {
      unsubscribe();
    };
  }, [fetchUserData]);

  return (
    <FireAuthContext.Provider value={{ user, loading, updateUser, updateUserField, lastUpdated }}>
      {children}
    </FireAuthContext.Provider>
  );
};

export const useFireAuth = () => useContext(FireAuthContext);
